import React, { useState, useEffect } from 'react';
import '../../styles/PageCommon.css';
import { ComposedChart, ReferenceArea, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Area } from 'recharts';
import CustomTooltip from './CustomTooltip';
import SliderToggle from '../common/SliderToggle/SliderToggle';
import './DemandPlanningReport.css';
import { formatIntoIsoDate, formatToAbbrevMonthYear, getOneMonthPrior, numberToKiloFormat } from '../../utils/utils';


export default function DemandPlanningReport({data, planningMonth, showPlanningPeriod = true}) {
  const todayDate = formatIntoIsoDate(new Date(Date.now()));
  const planningPeriodStartDate= getOneMonthPrior(planningMonth);
  const planningPeriodEndDate = planningMonth;
  
  const [activeLineId, setActiveLineId] = useState(null);
  const [showStockArea, setShowStockArea] = useState(true);
  const [showHubStockArea, setShowHubStockArea] = useState(true);
  
  const [plotData, setPlotData] = useState([]);

  useEffect(() => {
    console.log("Received data for plotting:", data);
    if(data){
      setPlotData(data)
    }
  }, [data]);
  
  const forecastDate = new Date(planningMonth ?? todayDate);
  const forecastMonthStart = new Date(forecastDate.getFullYear(), forecastDate.getMonth(), 1);
  
  const futureLabelIndex = plotData.findIndex(item => {
    const d = new Date(item.date);
    const itemMonthStart = new Date(d.getFullYear(), d.getMonth(), 1);
    
    return itemMonthStart >= forecastMonthStart;
  });

  const renderCustomLegend = (props) => {    
    return (
      <div style={{ display: 'flex', marginTop: 16, justifyContent: 'center', gap: '24px', alignItems: 'center', alignSelf: 'stretch' }}>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: 4, paddingRight: 8, paddingTop: 2, paddingBottom: 2 }}>          
          <div style={{ width: 16, height: 4, backgroundColor: "#75D7A2", borderRadius: '6px' }} />
          <span style={{ fontSize: '13px', color: '#1C1C1C', fontStyle: "normal", fontWeight: "400", lineHeight: "150%"}}>Факт</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: 4, paddingRight: 8, paddingTop: 2, paddingBottom: 2 }}>          
          <div style={{ width: 16, display: "flex", alignItems: "center", gap: "2px"}}>
            <div style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: "#75D7A2" }}></div>
            <div style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: "#75D7A2" }}></div>
            <div style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: "#75D7A2" }}></div>
          </div>
          <span style={{ fontSize: '13px', color: '#1C1C1C', fontStyle: "normal", fontWeight: "400", lineHeight: "150%"}}>Прогноз (базовый)</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: 4, paddingRight: 8, paddingTop: 2, paddingBottom: 2 }}>          
          <div style={{ width: 16, height: 4, backgroundColor: "#F771AF", borderRadius: '6px' }} />
          <span style={{ fontSize: '13px', color: '#1C1C1C', fontStyle: "normal", fontWeight: "400", lineHeight: "150%"}}>Цель</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: 4, paddingRight: 8, paddingTop: 2, paddingBottom: 2 }}>
          <div style={{ width: 16, display: "flex", alignItems: "center", gap: "2px"}}>
            <div style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: "#F771AF" }}></div>
            <div style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: "#F771AF" }}></div>
            <div style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: "#F771AF" }}></div>
          </div>
          <span style={{ fontSize: '13px', color: '#1C1C1C', fontStyle: "normal", fontWeight: "400", lineHeight: "150%"}}>Прогноз (скорректированный)</span>
        </div>
        
        <div style={{border: "1px solid #E7E7E7", borderRadius: 16, paddingLeft: 8, paddingRight: 8, paddingTop: 6, paddingBottom: 6, display: 'flex', }}>
          <div style={{ width: 6, height: 6, backgroundColor: "#6AD2FF", borderRadius: '6px', margin: 'auto', marginRight: 12 }} />
          <SliderToggle 
            label="Доступный товар" 
            isOn={showStockArea} 
            handleToggle={() => setShowStockArea(!showStockArea)} 
            color="#7CC805"
          />
        </div>

        <div style={{border: "1px solid #E7E7E7", borderRadius: 16, paddingLeft: 8, paddingRight: 8, paddingTop: 6, paddingBottom: 6, display: 'flex', }}>
          <div style={{ width: 6, height: 6, backgroundColor: "#ffa857", borderRadius: '6px', margin: 'auto', marginRight: 12 }} />
          <SliderToggle 
            label="Доступный товар в РЦ" 
            isOn={showHubStockArea} 
            handleToggle={() => setShowHubStockArea(!showHubStockArea)} 
            color="#7CC805"
          />
        </div>

      </div>
    );
  };

  return (
    <div className='dp-chart-table-container custom-scrollbar'>
      <div className='dp-chart-container' style={{marginTop: "22px", minWidth: plotData.length >= 32 ? "2300px" :plotData.length >= 26 ? "2000px" : plotData.length >= 20 ? "1800px" : plotData.length >= 14 ? "1500px" : "1256px"}}>
        <ResponsiveContainer  minHeight={446} 
          style={{
            backgroundColor: '#FFF', border: "1px solid #E7E7E7", outline: 'none',
            width: "auto", borderRadius: "20px", padding: "0px", margin: "0px"
          }}
        >
          <ComposedChart
            data={plotData}
            margin={{ top: 30, bottom: 26, right: 30, left: 12 }}
          >
            <CartesianGrid vertical={false} stroke='#E7E7E7' strokeWidth={1}/>

            <XAxis dataKey="date" 
              tickFormatter={formatToAbbrevMonthYear} 
              interval={0}
              tickLine={false}
              axisLine={false}
              tick={{ 
                fill: '#898989', 
                fontSize: plotData.length > 24 ? 11 : 12,
                fontWeight: 400, 
                letterSpacing: "0.12px" 
              }}
              tickMargin={16}
            />
            <YAxis 
              domain={[0, 'auto']} 
              tickLine={false}
              axisLine={false}
              tick={(props) => {
                const { x, y, payload } = props;

                const formatted = numberToKiloFormat(payload.value, { precision: 2 });

                return (
                  <text
                    x={x}
                    y={y}
                    textAnchor="end"
                    dominantBaseline="middle"
                    fill="#898989"
                    fontSize={12}
                    fontWeight={400}
                    letterSpacing="0.12px"
                  >
                    {formatted}
                  </text>
                );
              }}
              tickMargin={1} 
            />

            <Tooltip content={<CustomTooltip activeLineId={activeLineId} />} 
              position={{ x: 0, y: 0 }}
              isAnimationActive={true}
              cursor={false}
              allowEscapeViewBox={{ x: true, y: true }}
            />

            <Legend content={renderCustomLegend}/>

            <defs>
              <linearGradient id="colorHubStock" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffa857" stopOpacity={0.5} />
                <stop offset="70%" stopColor="#ffa857" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#ffa857" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorStock" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#57C3FF" stopOpacity={1} />
                <stop offset="70%" stopColor="#57C3FF" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#57C3FF" stopOpacity={0.3} />
              </linearGradient>
            </defs>

            {/* Available stock --------- */}
            {showStockArea && (
              <Area
                type="monotone"
                key={`stock_${showStockArea}_${showHubStockArea}`}
                dataKey="stock"
                stackId="available-stock"
                stroke="none"
                fill="url(#colorStock)"
                dot={false}
                activeDot={false}
                isAnimationActive={true}
                animationDuration={500}
              />
            )}

            {/* Available Hub stock --------- */}
            {showHubStockArea && (
              <Area
                type="monotone"
                dataKey="hub_stock"
                stackId="available-stock"
                key={`hub_stock_${showStockArea}_${showHubStockArea}`}
                stroke="none"
                fillOpacity={1}
                fill="url(#colorHubStock)"
                dot={false}
                activeDot={false}
                isAnimationActive={true}
                animationDuration={500}
              />
            )}

            {/* --- Planning Period --- */}
            {
              showPlanningPeriod && 
                <>
                  <ReferenceLine
                    x={planningPeriodStartDate}
                    stroke="#DEDEDE"
                    isAnimationActive={false}
                  />
                  <ReferenceArea
                    x1={planningPeriodStartDate}
                    x2={planningPeriodEndDate}
                    fill="transparent"
                    label={{
                      value: 'Период планирования',
                      position: 'top',
                      offset: 5,
                      fill: '#6D6D6D',
                      fontSize: 12,
                      fontWeight: 400,
                    }}
                  />
                  <ReferenceLine
                    x={planningPeriodEndDate}
                    stroke="#DEDEDE"
                    strokeDasharray="5 5"                    
                  />
                </>
            }

            {/* Fact, Target lines */}
            <Line type="monotone" dataKey="factual" stroke="#75D7A2" 
              strokeWidth={3} 
              onMouseEnter={() => setActiveLineId('factual')}
              onMouseLeave={() => setActiveLineId(null)}
              connectNulls={true}
              activeDot={{
                r: activeLineId === 'factual' ? 8 : 0,
                onMouseEnter: () => setActiveLineId('target'),
                onMouseLeave: () => setActiveLineId(null),
              }}
              dot={false}
              isAnimationActive={true}
              animationDuration={1000}
            />
            <Line type="monotone" dataKey="target" stroke="#FD73B3"  
              strokeWidth={3} 
              activeDot={{
                r: activeLineId === 'target' ? 8 : 0,
                onMouseEnter: () => setActiveLineId('target'),
                onMouseLeave: () => setActiveLineId(null),
              }}
              onMouseEnter={() => setActiveLineId('target')}
              onMouseLeave={() => setActiveLineId(null)}
              connectNulls={true}
              dot={false}
              isAnimationActive={true}
              animationDuration={1000}
            />

            {/* 2. Forecast future lines */}
            <Line type="monotone" dataKey="forecast" stroke="#75D7A2" 
              dot={false} 
              strokeWidth={3} 
              strokeDasharray="8 8"
              strokeLinecap="round"
              activeDot={{
                r: activeLineId === 'forecast' ? 8 : 0,
                onMouseEnter: () => setActiveLineId('forecast'),
                onMouseLeave: () => setActiveLineId(null),
              }}
              onMouseEnter={() => setActiveLineId('forecast')}
              onMouseLeave={() => setActiveLineId(null)}
              isAnimationActive={true}
              animationDuration={1000}
              />
            <Line type="monotone" dataKey="adjustedForecast" stroke="#FD73B3" 
              dot={false} 
              strokeWidth={3} 
              strokeDasharray="7 7"
              strokeLinecap="round"
              activeDot={{
                r: activeLineId === 'adjustedForecast' ? 8 : 0,
                onMouseEnter: () => setActiveLineId('adjustedForecast'),
                onMouseLeave: () => setActiveLineId(null),
              }}
              onMouseEnter={() => setActiveLineId('adjustedForecast')}
              onMouseLeave={() => setActiveLineId(null)}
              isAnimationActive={true}
              animationDuration={1000}
              />

          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <table className="data-table dp-table" style={{minWidth: plotData.length >= 32 ? "2300px" :plotData.length >= 26 ? "2000px" : plotData.length >= 20 ? "1800px" : plotData.length >= 14 ? "1500px" : "1256px"}}>
        <thead>
          <tr>
            {
              plotData?.map((item, index) => (
                <th 
                  key={`header-row` + index}
                  className='dp-table-header-cell' style={{
                  width: Math.floor(100/plotData.length) + "%",
                  fontSize: plotData.length > 24 ? 10 : plotData.length > 14 ? 12 : 13, 
                  padding: 0,
                  // paddingLeft: index === 0 ? (plotData.length >= 18 ? 4 : 10) : 2
                }}>
                  <div className="dp-table-cell" style={{
                    padding: plotData.length >= 24 ? "12px 2px 12px 9px" : plotData.length > 12 ? "12px 10px" : "12px 20px",
                    // paddingLeft: (index === 0 && plotData.length > 20) ? 4 : undefined,
                    whiteSpace: "nowrap",
                  }}>
                    {formatToAbbrevMonthYear(item.date)}
                  </div>
                </th>
              ))
            }
            {
              Array.from({ length: Math.max(0, 2 - (plotData.length - 1 - futureLabelIndex)) }).map((_, i) => (
                <th 
                  key={`header-row`+i}
                  style={{
                  width: Math.floor(100/plotData.length) + "%",
                }}>
                  <div className="dp-table-cell" style={{
                    padding: plotData.length >= 24 ? "20px 2px 20px 9px" : plotData.length > 12 ? "20px 10px" : "18px 20px",
                    whiteSpace: "nowrap",
                  }}>
                  </div>
                </th>
              ))
            }
          </tr>
        </thead>
        <tbody>
          <tr key={`fact-row`}>
            {
              plotData?.map((item, index) => (
                <td 
                  key={`fact-row` + index}
                  style={{
                  borderLeft: index === futureLabelIndex && index !== 0 ? "1px solid #E7E7E7" : "",
                  width: Math.floor(100/plotData.length) + "%",
                  padding: 0,
                  // paddingLeft: index === 0 ? (plotData.length < 18 ? 8 : 2) : 0,
                  borderBottom: "1px solid #E7E7E7"
                }}>
                  <div className="dp-table-cell" style={{
                    color: index < futureLabelIndex ? "#898989" : "#010101",
                    padding: plotData.length >= 24 ? "20px 2px 20px 9px" : plotData.length > 12 ? "20px 10px" : "18px 20px",
                  }}>
                    {
                      index === 0 ? 
                        <span className='dp-table-cell-label'>{ index !== futureLabelIndex ? "Факт" : "\u00A0"}</span> 
                        : index < futureLabelIndex ? 
                          <span className='dp-table-cell-label'>{ "\u00A0"}</span> 
                          : index > futureLabelIndex ? 
                            <span className='dp-table-cell-label'>{ "\u00A0"}</span> : ""
                    }
                    {
                      index === futureLabelIndex ? 
                        <span className='dp-table-cell-label'>Базовый прогноз</span> : ""
                    } 
                    <div className='dp-table-cell-content'
                      title={index >= futureLabelIndex ? 
                          item.forecast?.toLocaleString("ru-RU")
                          : item.factual?.toLocaleString("ru-RU")}  
                    >
                      {
                        futureLabelIndex === -1 ?
                          numberToKiloFormat(item.factual, { unitStyle: { fontSize: "clamp(9px, 18cqi, 12px)"} })
                          :
                        index >= futureLabelIndex ? 
                          numberToKiloFormat(item.forecast, { unitStyle: { fontSize: "clamp(9px, 18cqi, 12px)"} })
                          : numberToKiloFormat(item.factual, { unitStyle: { fontSize: "clamp(9px, 18cqi, 12px)"} })
                      }
                    </div>
                  </div>
                </td>
              ))
            }
            {
              Array.from({ length: Math.max(0, 2 - (plotData.length - 1 - futureLabelIndex)) }).map((_, i) => (
                <td
                  key={`aux-fact-row` + i}
                  style={{
                    width: Math.floor(100 / plotData.length) + "%",
                    borderBottom: "1px solid #E7E7E7"
                  }}
                >
                  <div
                    className='dp-table-cell'
                    style={{
                      padding: plotData.length >= 24 ? "20px 2px 20px 9px" : plotData.length > 12 ? "20px 10px" : "18px 20px",
                    }}
                  />
                </td>
              ))
            }
          </tr>
          <tr key={`target-row`}>
            {
              plotData?.map((item, index) => (
                <td 
                  key={`target-row` + index}
                  style={{
                  borderLeft: index === futureLabelIndex && index !== 0 ? "1px solid #E7E7E7" : "",
                  width: Math.floor(100/plotData.length) + "%",
                  padding: 0,
                  // paddingLeft: index === 0 ? (plotData.length < 18 ? 8 : 2) : 0,
                  borderBottom: (showStockArea || showHubStockArea) && "1px solid #E7E7E7"
                }}>
                  <div className='dp-table-cell' style={{
                    color: index < futureLabelIndex ? "#898989" : "#010101",
                    padding: plotData.length >= 24 ? "20px 2px 20px 9px" : plotData.length > 12 ? "20px 10px" : "18px 20px",
                  }}>
                    {
                      index === 0 ? 
                        <span className='dp-table-cell-label'>{ index !== futureLabelIndex ? "Цель" : "\u00A0"}</span> 
                        : index < futureLabelIndex ? 
                          <span className='dp-table-cell-label'>{ "\u00A0"}</span> 
                          : index > futureLabelIndex ? 
                            <span className='dp-table-cell-label'>{ "\u00A0"}</span> : ""
                    }
                    {                    
                      index === futureLabelIndex ? 
                        <span className='dp-table-cell-label'>Скорректированный прогноз</span> : ""
                    } 
                    <div className='dp-table-cell-content' 
                      title={index >= futureLabelIndex ? 
                          item.adjustedForecast?.toLocaleString("ru-RU")
                          : item.target?.toLocaleString("ru-RU")}
                    >
                      {
                        futureLabelIndex === -1 ?
                          numberToKiloFormat(item.target, { unitStyle: { fontSize: "clamp(9px, 18cqi, 12px)"} })
                          :
                        index >= futureLabelIndex ? 
                          numberToKiloFormat(item.adjustedForecast, { unitStyle: { fontSize: "clamp(9px, 18cqi, 12px)"} })
                          : numberToKiloFormat(item.target, { unitStyle: { fontSize: "clamp(9px, 18cqi, 12px)"} })
                      }
                    </div>
                  </div>
                </td>
              ))
            }
            {
              Array.from({ length: Math.max(0, 2 - (plotData.length - 1 - futureLabelIndex)) }).map((_, i) => (
                <td
                  key={`aux-target-row`+i}
                  style={{
                    width: Math.floor(100 / plotData.length) + "%",
                    borderBottom: (showStockArea || showHubStockArea) && "1px solid #E7E7E7"
                  }}
                >
                  <div
                    className='dp-table-cell'
                    style={{
                      padding: plotData.length >= 24 ? "20px 2px 20px 9px" : plotData.length > 12 ? "20px 10px" : "18px 20px",
                    }}
                  />
                </td>
              ))
            }
          </tr>
          
          {
            showStockArea && 
              <tr key={`area-row`}>
                  {
                    plotData?.map((item, index) => (
                      <td 
                        key={`area-row` + index}
                        style={{
                          width: Math.floor(100/plotData.length) + "%",
                          padding: 0,
                          // paddingLeft: index === 0 ? (plotData.length < 18 ? 8 : 2) : 0,
                          borderBottom: showHubStockArea && "1px solid #E7E7E7"
                      }}>
                        <div  className='dp-table-cell' style={{
                          color: index < futureLabelIndex ? "#898989" : "#010101",
                          padding: plotData.length >= 24 ? "20px 2px 20px 9px" : plotData.length > 12 ? "20px 10px" : "18px 20px",
                        }}>                            
                          {
                            <span className='dp-table-cell-label'>{index === 0 ? "Доступный товар" : "\u00A0"}</span>
                          } 
                          <div className='dp-table-cell-content'
                            title={item.stock?.toLocaleString("ru-RU")}  
                          >
                            {
                              numberToKiloFormat(item.stock, { unitStyle: { fontSize: "clamp(9px, 18cqi, 12px)"} })
                            }
                          </div>
                        </div>
                      </td>
                    ))
                  }
                  {
                    Array.from({ length: Math.max(0, 2 - (plotData.length - 1 - futureLabelIndex)) }).map((_, i) => (
                      <td
                        key={`aux-area-row`+i}
                        style={{
                          width: Math.floor(100 / plotData.length) + "%",
                          borderBottom: showHubStockArea && "1px solid #E7E7E7"
                        }}
                      >
                        <div
                          className='dp-table-cell'
                          style={{
                            padding: plotData.length >= 24 ? "20px 2px 20px 9px" : plotData.length > 12 ? "20px 10px" : "18px 20px",
                          }}
                        />
                      </td>
                    ))
                  }
              </tr>
          }
          {
            showHubStockArea && 
              <tr key={`hub-area-row`}>
                  {
                    plotData?.map((item, index) => (
                      <td 
                        key={`hub-area-row` + index}
                        style={{
                          width: Math.floor(100/plotData.length) + "%",
                          padding: 0,
                          // paddingLeft: index === 0 ? (plotData.length < 18 ? 8 : 2) : 0,
                      }}>
                        <div  className='dp-table-cell' style={{
                          color: index < futureLabelIndex ? "#898989" : "#010101",
                          padding: plotData.length >= 24 ? "20px 2px 20px 9px" : plotData.length > 12 ? "20px 10px" : "18px 20px",
                        }}>                            
                          {
                            <span className='dp-table-cell-label'>{index === 0 ? "Доступный товар в РЦ" : "\u00A0"}</span>
                          } 
                          <div className='dp-table-cell-content'
                            title={item.hub_stock?.toLocaleString("ru-RU")}  
                          >
                            {
                              numberToKiloFormat(item.hub_stock, { unitStyle: { fontSize: "clamp(9px, 18cqi, 12px)"} })
                            }
                          </div>
                        </div>
                      </td>
                    ))
                  }
                  {
                    Array.from({ length: Math.max(0, 2 - (plotData.length - 1 - futureLabelIndex)) }).map((_, i) => (
                      <td
                        key={`aux-area-row`+i}
                        style={{
                          width: Math.floor(100 / plotData.length) + "%",
                          borderBottom: "1px solid #E7E7E7"
                        }}
                      >
                        <div
                          className='dp-table-cell'
                          style={{
                            padding: plotData.length >= 24 ? "20px 2px 20px 9px" : plotData.length > 12 ? "20px 10px" : "18px 20px",
                          }}
                        />
                      </td>
                    ))
                  }
              </tr>
          }
        </tbody>
      </table>
    </div>
  );
}

